import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, Loader2, CheckCircle2, MapPin, Calendar, Phone, Copy, Check, Download } from "lucide-react";

function rupiah(n) {
  try { return "Rp " + Number(n || 0).toLocaleString("id-ID"); }
  catch { return "Rp " + n; }
}
function fmtDate(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return iso.slice(0, 10); }
}

function paymentPill(pm) {
  const p = (pm || "").toUpperCase();
  // v84.171 — Design C: saturated solid pill dgn text putih (bold brand-forward)
  const map = {
    CASH:     { bg: "bg-emerald-600", text: "text-white", border: "border-emerald-700", label: "Tunai" },
    TRANSFER: { bg: "bg-blue-600",    text: "text-white", border: "border-blue-700",    label: "Transfer" },
    QRIS:     { bg: "bg-fuchsia-600", text: "text-white", border: "border-fuchsia-700", label: "QRIS" },
  };
  return map[p] || { bg: "bg-zinc-700", text: "text-white", border: "border-zinc-800", label: p || "-" };
}

// Convert Google Drive share/view URL → direct thumbnail URL (works untuk foto preview inline)
function driveThumbUrl(u) {
  if (!u) return u;
  const m = u.match(/[-\w]{25,}/);
  if (!m) return u;
  return `https://drive.google.com/thumbnail?id=${m[0]}&sz=w800`;
}

export default function ViewInvoice() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  // v84.105 — Copy state utk tombol copy rekening
  const [copiedRek, setCopiedRek] = useState(false);
  const BANK_ACCOUNT_NUMBER = "0310010390097";
  const BANK_ACCOUNT_LABEL  = "Rekening Mandiri · Global AC";

  async function copyRek() {
    try {
      await navigator.clipboard.writeText(BANK_ACCOUNT_NUMBER);
      setCopiedRek(true);
      setTimeout(() => setCopiedRek(false), 2000);
    } catch {
      // Fallback: gunakan legacy execCommand
      const ta = document.createElement("textarea");
      ta.value = BANK_ACCOUNT_NUMBER;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setCopiedRek(true); setTimeout(() => setCopiedRek(false), 2000); }
      finally { document.body.removeChild(ta); }
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const r = await api.publicInvoice(token);
        setData(r);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Invoice tidak ditemukan");
      }
    })();
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-6" data-testid="invoice-error-page">
        <div className="max-w-md bg-white rounded-2xl shadow-lg p-8 text-center border border-border">
          <div className="text-4xl mb-3">📄</div>
          <div className="font-['Manrope'] font-bold text-lg mb-1">Invoice Tidak Ditemukan</div>
          <div className="text-[13px] text-zinc-500">{err}</div>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center" data-testid="invoice-loading">
        <div className="flex items-center gap-2 text-zinc-500 text-[13px]">
          <Loader2 className="w-4 h-4 animate-spin" />
          Memuat invoice…
        </div>
      </div>
    );
  }

  const pmPill = paymentPill(data.payment_method);
  const photos = (data.photo_urls || []).slice(0, 6);
  const warrantyItems = (data.items || []).filter(it => it.warranty_label || it.warranty_coverage);

  return (
    <div className="min-h-screen bg-zinc-100 print:bg-white print:p-0" data-testid="view-invoice-page">
      {/* Print action bar - hidden when printing */}
      <div className="bg-white border-b border-border sticky top-0 z-10 print:hidden">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-bold">Invoice Digital</div>
          <div className="text-[11px] text-zinc-400 font-mono truncate">#{token?.slice(0, 12)}</div>
          <Button
            onClick={() => window.print()}
            className="ml-auto bg-[#E65100] hover:bg-[#F57C00] rounded-sm gap-1.5 h-9"
            data-testid="invoice-print-btn"
          >
            <Printer className="w-3.5 h-3.5" />
            Simpan / Cetak PDF
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 print:p-6 print:max-w-full">
        <div className="bg-white shadow-xl print:shadow-none print:rounded-none overflow-hidden rounded-2xl print:border-0" data-testid="invoice-card" style={{ boxShadow: "0 25px 50px -12px rgba(0,0,0,0.15)" }}>
          {/* v84.171 — Design C: Black hero header dgn brand orange + INVOICE label besar */}
          {/* v84.172 — Info kantor pusat ditambah sbg baris horizontal tipis di dalam header hitam */}
          <div className="px-6 sm:px-8 pt-5 pb-3" style={{ background: "#0A0E1A" }}>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 sm:gap-4">
                <div
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center shrink-0"
                  style={{ background: "linear-gradient(135deg, #FF6B35, #E65100)" }}
                >
                  <svg className="w-7 h-7 sm:w-8 sm:h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/>
                  </svg>
                </div>
                <div>
                  <div className="font-['Manrope'] font-black text-xl sm:text-2xl text-white leading-none tracking-tight" data-testid="brand-name">
                    GLOBAL AC
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold mt-1.5" style={{ color: "#FDBA74" }}>
                    Banjarbaru · Service Center
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-['Manrope'] font-black text-2xl sm:text-3xl text-white leading-none">INVOICE</div>
                <div className="font-mono text-[11px] sm:text-[12px] mt-1.5 font-semibold" style={{ color: "#FDBA74" }}>#{(token || "").slice(0, 12)}</div>
                <div className="text-[11px] text-white/70 mt-0.5">{fmtDate(data.submitted_at || data.scheduled_start)}</div>
              </div>
            </div>
            {/* v84.172 — Head Office info strip (horizontal, compact, wajib legal) */}
            <div
              className="mt-3 pt-2.5 border-t border-white/10 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[9px] sm:text-[10px] leading-snug text-white/80 print:text-white/90"
              data-testid="invoice-head-office"
            >
              <span className="font-bold uppercase tracking-wider text-white/90">Head Office</span>
              <span className="text-white/30">·</span>
              <span>Jl. Trikora, Komp. Abaditama No.112 RT.39, Banjarbaru, Kalimantan Selatan 70721</span>
              <span className="text-white/30">·</span>
              <span className="font-mono">☎ 0811582226 / 081349778691</span>
              <span className="text-white/30">·</span>
              <span className="font-mono">NPWP CV. Global AC: 71.193.439.8-732.000</span>
            </div>
          </div>

          {/* Orange info strip */}
          <div className="px-6 sm:px-8 py-2.5 flex items-center gap-4 sm:gap-6 text-white text-[11px] sm:text-[12px] font-semibold flex-wrap"
               style={{ background: "linear-gradient(90deg, #E65100 0%, #F57C00 100%)" }}>
            {data.technician?.name && (
              <div className="flex items-center gap-1.5"><span className="text-white/85">🚐</span> {data.technician.name.toUpperCase()}</div>
            )}
            <div className="w-px h-4 bg-white/30 hidden sm:block"></div>
            <div className="flex items-center gap-1.5"><span className="text-white/85">✓</span> Teknisi Terverifikasi</div>
          </div>

          {/* Customer & jadwal — high-contrast zinc-800 labels */}
          <div className="px-6 sm:px-8 py-5 grid grid-cols-1 sm:grid-cols-2 gap-5 border-b border-zinc-200 text-[13px]">
            <div>
              <div className="text-[12px] uppercase tracking-wider text-zinc-800 font-bold mb-1.5">Ditagihkan Kepada</div>
              <div className="font-bold text-zinc-900 text-base leading-tight" data-testid="invoice-customer-name">
                {data.customer_name || "Customer"}
              </div>
              {data.customer_phone && (
                <div className="flex items-center gap-1.5 text-[12px] text-zinc-700 font-medium mt-1.5">
                  <Phone className="w-3.5 h-3.5" /> {data.customer_phone}
                </div>
              )}
              {data.address && (
                <div className="flex items-start gap-1.5 text-[12px] text-zinc-700 mt-1">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span data-testid="invoice-address">{data.address}</span>
                </div>
              )}
            </div>
            <div className="sm:text-right">
              <div className="text-[12px] uppercase tracking-wider text-zinc-800 font-bold mb-1.5">Jadwal Layanan</div>
              <div className="flex items-center gap-1.5 sm:justify-end text-[13px] text-zinc-900 font-semibold">
                <Calendar className="w-3.5 h-3.5" /> {fmtDate(data.scheduled_start)}
              </div>
              {data.technician?.name && (
                <div className="text-[12px] text-zinc-700 mt-1">
                  Teknisi: <span className="font-bold text-zinc-900">{data.technician.name}</span>
                </div>
              )}
            </div>
          </div>

          {/* Items — Design C black header row */}
          <div className="px-6 sm:px-8 py-5 border-b border-zinc-200" data-testid="invoice-items">
            <div className="text-[12px] uppercase tracking-wider text-zinc-800 font-bold mb-3">Rincian Pekerjaan</div>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-white text-[10px] sm:text-[11px] uppercase tracking-wider font-bold" style={{ background: "#0A0E1A" }}>
                  <th className="text-left py-2.5 px-3 rounded-l-md">Item</th>
                  <th className="text-center py-2.5 w-12">Qty</th>
                  <th className="text-right py-2.5 w-24">Harga</th>
                  <th className="text-right py-2.5 px-3 w-28 rounded-r-md">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {(data.items || []).map((it, i) => (
                  <tr
                    key={i}
                    className={`border-b border-zinc-100 ${i % 2 === 1 ? "bg-zinc-50" : ""}`}
                    data-testid={`invoice-item-${i}`}
                  >
                    <td className="py-2.5 px-3">
                      <div className="font-bold text-zinc-900">{it.name}</div>
                      {it.warranty_label && (
                        <div className="text-[11px] mt-0.5">
                          <span className="text-zinc-500 font-mono">Garansi:</span>{" "}
                          <span className="text-emerald-700 font-bold">{it.warranty_label}</span>
                        </div>
                      )}
                    </td>
                    <td className="text-center py-2.5 font-mono font-bold text-zinc-900">{it.qty || 1}</td>
                    <td className="text-right py-2.5 font-mono text-zinc-800 font-semibold">{rupiah(it.price)}</td>
                    <td className="text-right py-2.5 px-3 font-mono font-bold text-zinc-900">
                      {rupiah(it.subtotal ?? (it.qty || 1) * (it.price || 0))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* BIG ORANGE TOTAL BLOCK */}
          <div
            className="px-6 sm:px-8 py-5 text-white flex items-center justify-between gap-3 flex-wrap"
            style={{ background: "linear-gradient(135deg, #E65100 0%, #FF6B35 100%)" }}
          >
            <div>
              <div className="text-[12px] sm:text-[13px] uppercase tracking-wider font-bold text-white/95">Total Tagihan</div>
              <div className="text-[11px] text-white/85 mt-0.5">Sudah termasuk semua biaya</div>
            </div>
            <div
              className="font-['Manrope'] font-black text-3xl sm:text-4xl leading-none tracking-tight"
              data-testid="invoice-grand-total"
            >
              {rupiah(data.grand_total)}
            </div>
          </div>

          {/* Payment method */}
          <div className="px-6 sm:px-8 py-4 bg-zinc-50 border-b border-zinc-200">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[12px] uppercase tracking-wider text-zinc-800 font-bold">Metode Pembayaran</div>
              <div
                className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-[12px] font-bold shadow-sm ${pmPill.bg} ${pmPill.text}`}
                data-testid="invoice-payment-method"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                {pmPill.label.toUpperCase()}
              </div>
            </div>

            {/* v71 — Tampilkan nomor rekening jika metode = Transfer (Design C card) */}
            {(data.payment_method || "").toLowerCase() === "transfer" && (
              <div
                className="mt-3 bg-white rounded-lg p-3 sm:p-4 border border-zinc-200 flex items-center justify-between gap-3 flex-wrap"
                data-testid="invoice-bank-account"
              >
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-zinc-700 font-bold">
                    {BANK_ACCOUNT_LABEL}
                  </div>
                  <div className="font-mono font-black text-xl sm:text-2xl text-zinc-900 mt-0.5">
                    {BANK_ACCOUNT_NUMBER}
                  </div>
                </div>
                <button
                  onClick={copyRek}
                  className={`relative inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[12px] font-bold shadow-sm text-white transition ${
                    copiedRek ? "bg-emerald-600" : "hover:opacity-90 active:scale-95"
                  }`}
                  style={!copiedRek ? { background: "#E65100" } : undefined}
                  data-testid="copy-rek-btn"
                  aria-label="Copy nomor rekening"
                >
                  {copiedRek ? <><Check className="w-3.5 h-3.5" /> COPIED</>
                             : <><Copy className="w-3.5 h-3.5" /> COPY REKENING</>}
                  {copiedRek && (
                    <span className="absolute -top-7 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[10px] font-bold px-2 py-0.5 rounded whitespace-nowrap shadow-md pointer-events-none">
                      Copied!
                    </span>
                  )}
                </button>
              </div>
            )}

            {data.notes && (
              <div className="mt-3 text-[12px] text-zinc-800 border-t border-zinc-200 pt-2.5">
                <span className="uppercase tracking-wider text-zinc-700 font-bold text-[10px]">Catatan:</span>{" "}
                <span className="font-medium">{data.notes}</span>
              </div>
            )}
          </div>

          {/* v84.107 — QRIS image block. v84.171 — Design C: fuchsia saturated */}
          {(data.payment_method || "").toUpperCase() === "QRIS" && data.qris_image_url && (
            <div className="px-6 sm:px-8 py-4 border-b border-zinc-200 bg-fuchsia-50 flex flex-col items-center gap-3"
                 data-testid="qris-block">
              <div className="text-[12px] uppercase tracking-wider text-fuchsia-800 font-bold text-center">
                Scan QRIS di bawah untuk pembayaran
              </div>
              <img
                src={data.qris_image_url}
                alt="QRIS payment"
                className="w-full max-w-xs rounded-md border-2 border-fuchsia-300 shadow-md bg-white p-2"
                data-testid="qris-image"
              />
              <a
                href={data.qris_image_url}
                download={`qris-invoice-${data.invoice_id || "payment"}.png`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-fuchsia-600 hover:bg-fuchsia-700 active:scale-95 text-white text-[12px] font-bold transition shadow-sm"
                data-testid="save-qris-btn"
              >
                <Download className="w-3.5 h-3.5" /> Simpan Gambar QRIS
              </a>
              <div className="text-[11px] text-fuchsia-800 text-center leading-relaxed font-medium">
                Buka aplikasi mobile banking / e-wallet Anda,<br />pilih menu Scan QRIS, lalu arahkan ke kode di atas.
              </div>
            </div>
          )}

          {/* Garansi — Design C high-contrast */}
          {warrantyItems.length > 0 && (
            <div className="px-6 sm:px-8 py-4 border-b border-zinc-200 bg-emerald-50" data-testid="invoice-warranty-section">
              <div className="flex items-center gap-2 text-[12px] uppercase tracking-wider text-emerald-800 font-bold mb-2">
                <CheckCircle2 className="w-4 h-4" />
                Informasi Garansi
              </div>
              <div className="space-y-2">
                {warrantyItems.map((it, i) => (
                  <div key={i} className="text-[13px]">
                    <div className="font-bold text-emerald-900">{it.name}</div>
                    {it.warranty_label && (
                      <div className="text-emerald-800 font-medium">Masa garansi: <b>{it.warranty_label}</b></div>
                    )}
                    {it.warranty_coverage && (
                      <div className="text-[12px] text-emerald-700 mt-0.5">
                        Hal yang dicover: {it.warranty_coverage}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Foto bukti servis — Design C */}
          {photos.length > 0 && (
            <div className="px-6 sm:px-8 py-4 border-b border-zinc-200" data-testid="invoice-photos-section">
              <div className="text-[12px] uppercase tracking-wider text-zinc-800 font-bold mb-2">Foto Bukti Servis</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {photos.map((u, i) => (
                  <a
                    key={i}
                    href={u}
                    target="_blank"
                    rel="noreferrer"
                    className="block aspect-square bg-zinc-100 rounded-lg overflow-hidden border border-zinc-200 hover:ring-2 hover:ring-orange-500 transition-all"
                    data-testid={`invoice-photo-${i}`}
                  >
                    <img
                      src={driveThumbUrl(u)}
                      alt={`Foto servis ${i + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.src = "/logo.png"; e.currentTarget.className = "w-full h-full object-contain p-4 opacity-30"; }}
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Footer — Design C high-contrast */}
          <div className="px-6 sm:px-8 py-5 text-center bg-zinc-50">
            <div className="text-[13px] text-zinc-800 font-bold">Terima kasih telah menggunakan Global AC Banjarbaru 🙏</div>
            <div className="text-[12px] text-zinc-700 mt-1.5 font-medium">
              Simpan invoice ini sebagai bukti pembayaran &amp; klaim garansi
            </div>
          </div>

          {/* Bottom black bar */}
          <div className="h-2" style={{ background: "#0A0E1A" }}></div>
        </div>
      </div>

      {/* Print CSS: hide non-print bits, expand card */}
      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:rounded-none { border-radius: 0 !important; }
          .print\\:bg-white { background: white !important; }
          .print\\:p-0 { padding: 0 !important; }
          .print\\:p-6 { padding: 1.5rem !important; }
          .print\\:border-0 { border: 0 !important; }
          .print\\:max-w-full { max-width: 100% !important; }
        }
      `}</style>
    </div>
  );
}
